import {ChecklistIcon, InfinityIcon, QuestionIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Tooltip} from '@primer/react'
import {useState} from 'react'

import styles from './CLIModePicker.module.css'

export type CLIAgentMode = 'interactive' | 'plan' | 'autopilot'

const MODE_INFO: Record<CLIAgentMode, {icon: React.ComponentType; label: string; description: string}> = {
  interactive: {
    icon: QuestionIcon,
    label: 'Interactive',
    description: 'Tool calls need approval to be executed',
  },
  plan: {
    icon: ChecklistIcon,
    label: 'Plan',
    description: 'Plan first, execute when ready',
  },
  autopilot: {
    icon: InfinityIcon,
    label: 'Autopilot',
    description: 'End-to-end execution without interruption',
  },
}

const MODES: CLIAgentMode[] = ['interactive', 'plan', 'autopilot']

interface CLIModePickerProps {
  selectedMode: CLIAgentMode
  onModeChange: (mode: CLIAgentMode) => void
  disabled?: boolean
}

export function CLIModePicker({selectedMode, onModeChange, disabled}: CLIModePickerProps) {
  const currentInfo = MODE_INFO[selectedMode]
  const [open, setOpen] = useState(false)

  const button = (
    <Button
      size="small"
      variant="invisible"
      trailingAction={TriangleDownIcon}
      aria-label={`Select CLI agent mode, current: ${currentInfo.label}`}
      inactive={disabled}
      aria-disabled={disabled}
    >
      <span className={styles.buttonLabel}>{currentInfo.label}</span>
    </Button>
  )

  return (
    <ActionMenu open={open} onOpenChange={nextOpen => !disabled && setOpen(nextOpen)}>
      <ActionMenu.Anchor>{disabled ? <Tooltip text="Switching mode...">{button}</Tooltip> : button}</ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        <ActionList>
          <ActionList.Group selectionVariant="single">
            {MODES.map(mode => {
              const info = MODE_INFO[mode]
              return (
                <ActionList.Item key={mode} selected={mode === selectedMode} onSelect={() => onModeChange(mode)}>
                  <ActionList.LeadingVisual>
                    <info.icon />
                  </ActionList.LeadingVisual>
                  {info.label}
                  <ActionList.Description>{info.description}</ActionList.Description>
                </ActionList.Item>
              )
            })}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
